import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import Header from "../../components/header";
import ShareModal from "../../components/modal/ShareModal";
import StickyActionBar from "../../components/StickyActionBar";
import { useTranslate } from "../../context/TranslationContext";
import usePageTheme from "../../hooks/usePageTheme";
import { useGetInstructorsQuery } from "../../redux/Apis/User";
import {
  useCreateUserFollowerMutation,
  useGetFollowersByUserIdQuery,
  useUpdateFollowerMutation,
} from "../../redux/Apis/UserFollower";
import { createThemeVars, THEME_OPTIONS } from "../../utils/theme";
import "./style.css";

const InstructorFollower = () => {
  const { t } = useTranslate("instructorDetails");
  const { id } = useParams();
  const location = useLocation();
  const isFollowingList = location.state?.type === "following";
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const { data: instructorData } = useGetInstructorsQuery(
    { id, params: {} },
    { refetchOnMountOrArgChange: true, skip: !id },
  );
  const creatorTheme =
    instructorData?.data?.theme || instructorData?.data?.user?.theme;
  const { selectedTheme, setSelectedTheme, currentTheme, pageThemeVars } =
    usePageTheme({ defaultTheme: creatorTheme });
  const { user } = useSelector((state) => state.authSlice);
  const [localFollowingMap, setLocalFollowingMap] = useState(new Map());
  const themeVars = useMemo(
    () => createThemeVars(currentTheme),
    [currentTheme],
  );
  const isLightTheme = useMemo(
    () => currentTheme?.isLight === true,
    [currentTheme],
  );

  // ---------------- API ----------------
  const {
    data: followersData,
    isFetching: isLoading,
    isError,
    refetch,
  } = useGetFollowersByUserIdQuery(
    { params: isFollowingList ? { created_by: id } : { userid: id } },
    { refetchOnMountOrArgChange: true, skip: !id },
  );

  const { data: myFollowingData, refetch: refetchMyFollowing } =
    useGetFollowersByUserIdQuery(
      { params: { created_by: user?._id } },
      { skip: !user?._id },
    );

  const [createUserFollower] = useCreateUserFollowerMutation();
  const [updateFollower] = useUpdateFollowerMutation();

  // ---------------- INIT FOLLOW STATE ----------------
  useEffect(() => {
    if (myFollowingData?.data) {
      const map = new Map();
      myFollowingData.data.forEach((item) => {
        map.set(item.user_id, item._id);
      });
      setLocalFollowingMap(map);
    }
  }, [myFollowingData]);

  const user_image = useMemo(() => {
    if (!user?.picture) return images.profile.placeholder;
    if (user.picture.startsWith("http")) return user.picture;
    return user.picture.startsWith("images/")
      ? `${process.env.REACT_APP_S3_BASE_URL}${user.picture}`
      : images.profile.placeholder;
  }, [user?.picture]);

  // ---------------- HANDLERS ----------------
  const handleFollowerClick = (follower) => {
    if (follower.role === "learner") {
      navigate(`/learner/${follower.username}`);
    } else {
      navigate(`/instructor/${follower.username}`);
    }
  };

  const handleFollow = async (follower) => {
    if (!user?._id) {
      toast.error(t("toast.loginFirst"));
      return;
    }

    const targetUserId =
      (isFollowingList ? follower.user_id : follower.created_by) ||
      follower._id;
    const existingFollowerId = localFollowingMap.get(targetUserId);

    try {
      if (existingFollowerId) {
        // UNFOLLOW
        setLocalFollowingMap((prev) => {
          const map = new Map(prev);
          map.delete(targetUserId);
          return map;
        });

        await updateFollower({
          id: existingFollowerId,
          payload: { follow: false },
        }).unwrap();

        toast.success(t("toast.unfollowSuccess"));
      } else {
        // FOLLOW
        const res = await createUserFollower({
          payload: {
            user: targetUserId,
            created_by: user._id,
            follow: true,
          },
          params: {},
        }).unwrap();

        const newId = res?.data?._id;

        setLocalFollowingMap((prev) => {
          const map = new Map(prev);
          map.set(targetUserId, newId);
          return map;
        });

        toast.success(t("toast.followSuccess"));
      }

      refetch();
      refetchMyFollowing();
    } catch (error) {
      if (existingFollowerId) {
        setLocalFollowingMap((prev) => {
          const map = new Map(prev);
          map.set(targetUserId, existingFollowerId);
          return map;
        });
      }
      console.log("error", error?.message, error?.data?.message);
      toast.error(
        error?.message || error?.data?.message || "Something went wrong",
      );
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("toast.copySuccess"));
    } catch (error) {
      toast.error(t("toast.copyFailed"));
    }
  };

  // ---------------- UI ----------------
  return (
    <>
      <Helmet>
        <title>
          {isFollowingList ? "Creator's Following" : "Creator's Followers"} |
          Traineego
        </title>
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/instructors" />
        <meta
          name="description"
          content={
            isFollowingList
              ? "Explore people this Creator follows on Traineego."
              : "Explore followers and community members connected with this Creator on Traineego."
          }
        />
      </Helmet>
      <Header
        showThemeToggle={true}
        themeOptions={THEME_OPTIONS}
        activeTheme={selectedTheme}
        onThemeChange={setSelectedTheme}
      />

      <div
        className={`min-vh-100 instructor-theme-page ${isLightTheme ? "instructor-theme-light" : ""}`}
        style={{
          padding: "150px 0 120px 0",
          ...pageThemeVars,
        }}
      >
        <h3 className="text-center mb-4">
          {t(isFollowingList ? "following" : "followers")}
        </h3>

        {isLoading && (
          <p className="text-center">
            <Spinner />
          </p>
        )}

        {!isLoading && (!followersData?.data?.length || isError) && (
          <p className="text-center text-muted">
            {t(isFollowingList ? "noFollowingFound" : "noFollowersFound")}
          </p>
        )}

        <div className="container px-3 px-sm-4">
          <div className="col-12 col-lg-10 col-xl-8 mx-auto">
            {!isLoading &&
              followersData?.data?.map((follower) => {
                const fullName = `${follower?.first_name || ""} ${
                  follower?.last_name || ""
                }`.trim();

                const initials = `${follower?.first_name?.[0] || ""}${
                  follower?.last_name?.[0] || ""
                }`;

                const targetUserId =
                  (isFollowingList ? follower.user_id : follower.created_by) ||
                  follower._id;
                const isFollowing = localFollowingMap.has(targetUserId);
                const isOwn = user?._id === targetUserId;
                const isVerified =
                  (follower?.verification_status ||
                    follower?.creator_obj?.verification_status) === "verified";

                return (
                  <div
                    key={follower._id}
                    onClick={() => handleFollowerClick(follower)}
                    className="d-flex align-items-center justify-content-between p-3 mb-3 rounded follower-row"
                    style={{
                      background: isLightTheme ? "#fff" : "#0e0e18",
                      cursor: "pointer",
                      color: isLightTheme ? "#000" : "#fff",
                      border: "none",
                      boxShadow: `
                      inset 0 1px 0 rgba(255, 255, 255, 0.14),
                      0 8px 20px rgba(0, 0, 0, 0.28)
                    `,
                    }}
                  >
                    <div className="follower-row-info">
                      <div className="follower-row-avatar">
                        {follower?.picture ? (
                          <img
                            src={follower.picture}
                            alt={fullName}
                            className="rounded-circle"
                            width="45"
                            height="45"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 45,
                              height: 45,
                              background: "#2a2a40",
                              color: "#fff",
                            }}
                          >
                            {initials || "U"}
                          </div>
                        )}
                      </div>

                      <div className="follower-row-text">
                        <p className="mb-0 follower-row-name d-flex align-items-center gap-2">
                          <span className="follower-row-name-text">
                            {fullName}
                          </span>
                          {isVerified && (
                            <span className="follower-name-verified">
                              <FontAwesomeIcon
                                icon={faCheck}
                                style={{ fontSize: "10px" }}
                              />
                            </span>
                          )}
                        </p>
                        <p
                          className="mb-0 text-muted follower-row-tagline"
                          style={{ fontSize: 14 }}
                        >
                          {follower?.tagline || ""}
                        </p>
                      </div>
                    </div>

                    {!isOwn && (
                      <button
                        className={`follower-row-action ${
                          isFollowing
                            ? `site-btn ${isLightTheme ? "light-following-btn" : "border-btn"}`
                            : "site-btn"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollow(follower);
                        }}
                      >
                        {isFollowing ? t("following") : t("follow")}
                      </button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <StickyActionBar
        user={user}
        userImage={user_image}
        themeVars={themeVars}
        onShare={() => setShowShareModal(true)}
        shareActive={showShareModal}
      />

      {/* Share Modal */}
      <ShareModal
        shareUrl={window.location.href}
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        t={t}
        themeVars={themeVars}
      />
    </>
  );
};

export default InstructorFollower;
